import { Box, TextField, Typography } from '@mui/material';
import { useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';
import { useUserPermissions } from '@hooks/useUserPermissions';
import { Roles } from '@rdTypes/shared';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import NotifyMailroomSwitch from 'src/components/NotifyMailroomSwitch';

import { ActionComponentProps } from '../../index';
import { CollapsibleOptions } from './CollapsibleOptions';

export const FinishDelivery = ({ mailpieces, actionStarted, onActionFinish }: ActionComponentProps) => {
  const { isAllowed } = useUserPermissions();
  const isAdmin = isAllowed([Roles.Admin]);
  const [updatePieces, { isLoading }] = useUpdatePiecesMutation();
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();
  const [skipNotifyRecipient, setSkipNotifyRecipient] = useState(false);
  const [comment, setComment] = useState('');
  const [notifyMailroom, setNotifyMailroom] = useState(true);

  const confirmAction = async () => {
    await updatePieces({
      pieces: mailpieces.map((piece) => ({ id: piece.id })),
      action: PieceAction.FinishDelivery,
      shouldNotifyRecipient: !skipNotifyRecipient,
    }).unwrap();

    if (comment.trim().length) {
      await createMailpiecesListComment({
        text: comment.trim(),
        notifyMailroom,
        pieceIds: mailpieces.map((piece) => piece.id),
      }).unwrap();
    }

    onActionFinish();
  };

  const content = (
    <Box>
      <Typography variant="body1" color="text.secondary">
        {mailpieces.length === 1 ? (
          <>Please deliver this item to the recipient, and click Complete when you&apos;re done.</>
        ) : (
          <>
            Please deliver <strong>{mailpieces.length}</strong> items to the recipient, and click Complete when
            you&apos;re done.
          </>
        )}
      </Typography>

      <CollapsibleOptions>
        <TextField
          sx={{ width: '100%', mt: 1, mb: 2 }}
          label="Optional Comment"
          multiline
          rows={3}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
        />
        <NotifyMailroomSwitch
          checked={notifyMailroom}
          onChange={() => setNotifyMailroom(!notifyMailroom)}
          label="Notify mailroom users about comment"
        />
        {isAdmin && (
          <Box sx={{ mt: 1 }}>
            <NotifyMailroomSwitch
              checked={skipNotifyRecipient}
              onChange={() => setSkipNotifyRecipient(!skipNotifyRecipient)}
              label="Skip notification to recipient about completed request"
            />
          </Box>
        )}
      </CollapsibleOptions>
    </Box>
  );

  return (
    <DialogConfirmation
      open={actionStarted}
      loading={isLoading}
      maxWidth="sm"
      title={mailpieces.length === 1 ? 'Deliver item' : 'Deliver selected items'}
      content={content}
      acceptButtonHandleClick={confirmAction}
      cancelButtonHandleClick={onActionFinish}
      acceptButtonTitle="Complete"
      cancelButtonTitle="Cancel"
    />
  );
};
